'use client';

import { useState, useEffect, useRef } from 'react';
import { BottomBar } from '@org/ui';
import { useLanguage } from '@/lib/config/useLanguage';
import useCart from '@/hooks/useCart';
import CatalogModal from '@/components/Layout/Header/CatalogModal';
import { usePathname } from 'next/navigation';
import { getBranchCodeFromPath, regionalizePath } from '@/lib/config/branches-config';

export default function BottomBarComponent({ catalogData }) {
    const { getNavigation, t, localizePath } = useLanguage();
    const { totalQuantity } = useCart();
    const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
    const bottomBarRef = useRef(null);
    const pathname = usePathname() || '/';
    const currentBranchCode = getBranchCodeFromPath(pathname);
    
    // Получаем элементы навигации для bottomBar
    const navigationItems = getNavigation('bottomBar') || [];
    
    // Функция для комбинирования языковой и региональной локализации
    const localizeAndRegionalizePath = (path) => {
        if (path === '#catalog-modal') {
            return '#catalog-modal';
        }
        const localizedPath = localizePath(path || '/');
        return regionalizePath(localizedPath, currentBranchCode);
    };
    
    // Функция для получения переводов
    const getTranslation = (key, fallback) => {
        // Пробуем получить перевод из common секции
        const translation = t(key, 'common');
        // Если перевод не найден, возвращаем fallback или сам ключ
        return translation !== key ? translation : (fallback || key);
    };
    
    // Функция для получения значения бейджа
    const getBadgeValue = (key) => {
        if (key === 'cart') {
            return totalQuantity > 0 ? totalQuantity : null;
        }
        return null;
    };
    
    // Обработчик клика для перехвата кликов на каталог
    useEffect(() => {
        const handleClick = (e) => {
            // Проверяем, был ли клик на ссылку с href === '#catalog-modal'
            const link = e.target.closest('a');
            if (link && (link.getAttribute('href') === '#catalog-modal' || link.href?.endsWith('#catalog-modal'))) {
                e.preventDefault();
                e.stopPropagation();
                setIsCatalogModalOpen(true);
                return false;
            }
        };
        
        const container = bottomBarRef.current;
        if (container) {
            // Используем capture фазу для перехвата кликов до того, как их обработает Link
            container.addEventListener('click', handleClick, true);
            return () => {
                container.removeEventListener('click', handleClick, true);
            };
        }
    }, []);
    
    if (navigationItems.length === 0) {
        return null;
    }
    
    return (
        <>
            <div ref={bottomBarRef}>
                <BottomBar
                    className="pb-[env(safe-area-inset-bottom)]"
                    items={navigationItems.map(item => ({
                        label: getTranslation(item.name, item.name),
                        href: localizeAndRegionalizePath(item.url || '/'),
                        icon: item.icon,
                        iconType: item.iconType,
                        badgeKey: item.badgeKey,
                    }))}
                    getTranslation={getTranslation}
                    getBadgeValue={getBadgeValue}
                />
            </div>
            <CatalogModal 
                open={isCatalogModalOpen} 
                onOpenChange={setIsCatalogModalOpen}
                categories={catalogData}
            />
        </>
    );
}
